import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../UI/Button';
import {
  WIDGET_TTS_HELP_TEXT,
  WIDGET_TTS_SAMPLE_PHRASE,
  WIDGET_TTS_SAVED_OPTION,
  buildTtsVoiceLabel,
  isPtBrVoiceLang,
  normTts,
  readSpeechError,
  resolveWidgetTtsVoice,
  savedTtsOptionValue,
  ttsOptionValue,
} from './widgetTtsVoice';

type VoiceCatalogPhase = 'unsupported' | 'loading' | 'ready';

interface WidgetTtsVoiceFieldProps {
  ttsVoiceUri?: string | null;
  ttsVoiceName?: string | null;
  onChange: (next: { ttsVoiceUri: string | null; ttsVoiceName: string | null }) => void;
}

function readVoiceCatalog(): { phase: VoiceCatalogPhase; voices: SpeechSynthesisVoice[] }
{
  if (typeof window === 'undefined'
    || !window.speechSynthesis
    || typeof window.speechSynthesis.getVoices !== 'function'
    || typeof window.SpeechSynthesisUtterance === 'undefined')
  {
    return { phase: 'unsupported', voices: [] };
  }

  try
  {
    const voices = Array.from(window.speechSynthesis.getVoices() || []);
    return {
      phase: voices.length === 0 ? 'loading' : 'ready',
      voices,
    };
  }
  catch
  {
    return { phase: 'unsupported', voices: [] };
  }
}

export const WidgetTtsVoiceField: React.FC<WidgetTtsVoiceFieldProps> = ({
  ttsVoiceUri,
  ttsVoiceName,
  onChange,
}) => {
  const initialCatalog = useMemo(() => readVoiceCatalog(), []);
  const [phase, setPhase] = useState<VoiceCatalogPhase>(initialCatalog.phase);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>(initialCatalog.voices);
  const sampleTokenRef = useRef(0);
  const sampleTimerRef = useRef<number | null>(null);
  const sampleRestartTimerRef = useRef<number | null>(null);
  const sampleListenerRef = useRef<(() => void) | null>(null);
  const sampleActiveRef = useRef(false);
  const cancelSampleRef = useRef<() => void>(() => {});

  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth || typeof synth.getVoices !== 'function' || typeof window.SpeechSynthesisUtterance === 'undefined')
    {
      setPhase('unsupported');
      setVoices([]);
      return;
    }

    let alive = true;
    const refresh = () => {
      if (!alive)
      {
        return;
      }

      try
      {
        const nextVoices = Array.from(synth.getVoices() || []);
        setVoices(nextVoices);
        setPhase(nextVoices.length === 0 ? 'loading' : 'ready');
      }
      catch
      {
        setPhase('unsupported');
        setVoices([]);
      }
    };

    refresh();
    const onVoicesChanged = () => {
      refresh();
    };
    synth.addEventListener('voiceschanged', onVoicesChanged);
    const retryTimer = window.setTimeout(refresh, 400);

    return () => {
      alive = false;
      synth.removeEventListener('voiceschanged', onVoicesChanged);
      window.clearTimeout(retryTimer);
    };
  }, []);

  useEffect(() => {
    return () => {
      cancelSampleRef.current();
    };
  }, []);

  const ptBrVoices = useMemo(() => {
    return voices
      .filter((voice) => isPtBrVoiceLang(voice.lang))
      .sort((left, right) => (left.name || '').localeCompare(right.name || '', 'pt-BR', { sensitivity: 'base' }));
  }, [voices]);

  const savedValue = savedTtsOptionValue(ttsVoiceUri, ttsVoiceName);
  const matched = savedValue !== '' && ptBrVoices.some((voice) => ttsOptionValue(voice) === savedValue);
  let selectValue = '';
  if (savedValue)
  {
    selectValue = matched ? savedValue : WIDGET_TTS_SAVED_OPTION;
  }

  const clearSampleRestart = () => {
    if (sampleRestartTimerRef.current !== null)
    {
      window.clearTimeout(sampleRestartTimerRef.current);
      sampleRestartTimerRef.current = null;
    }
  };

  const clearSampleWait = () => {
    if (sampleTimerRef.current !== null)
    {
      window.clearTimeout(sampleTimerRef.current);
      sampleTimerRef.current = null;
    }

    const synth = window.speechSynthesis;
    if (sampleListenerRef.current && synth)
    {
      synth.removeEventListener('voiceschanged', sampleListenerRef.current);
      sampleListenerRef.current = null;
    }
  };

  const cancelSample = () => {
    sampleTokenRef.current += 1;
    sampleActiveRef.current = false;
    clearSampleRestart();
    clearSampleWait();
    try
    {
      window.speechSynthesis?.cancel();
    }
    catch
    {
      return;
    }
  };

  cancelSampleRef.current = cancelSample;

  const playSample = () => {
    const synth = window.speechSynthesis;
    if (!synth || typeof window.SpeechSynthesisUtterance === 'undefined')
    {
      return;
    }

    const replaceSpoken = sampleActiveRef.current && (synth.speaking || synth.pending);
    if (sampleActiveRef.current)
    {
      cancelSample();
    }

    sampleActiveRef.current = true;
    const generation = sampleTokenRef.current;
    const uri = ttsVoiceUri;
    const name = ttsVoiceName;

    const startSample = () => {
      if (generation !== sampleTokenRef.current)
      {
        return;
      }

      const speak = (voice: SpeechSynthesisVoice | null) => {
      const generation = sampleTokenRef.current;
      const utterance = new SpeechSynthesisUtterance(WIDGET_TTS_SAMPLE_PHRASE);
      utterance.lang = 'pt-BR';
      utterance.rate = 0.95;
      utterance.pitch = 1;
      utterance.volume = 0.9;
      let voiceApplied = false;
      if (voice)
      {
        try
        {
          utterance.voice = voice;
          voiceApplied = true;
        }
        catch
        {
          voiceApplied = false;
        }
      }

      utterance.onstart = () => {
        if (generation !== sampleTokenRef.current)
        {
          return;
        }

        sampleActiveRef.current = true;
      };

      utterance.onend = () => {
        if (generation !== sampleTokenRef.current)
        {
          return;
        }

        sampleActiveRef.current = false;
      };

      let retried = false;
      utterance.onerror = (event) => {
        const errorCode = readSpeechError(event);
        const shouldRetry = voiceApplied
          && !retried
          && generation === sampleTokenRef.current
          && errorCode !== 'canceled'
          && errorCode !== 'interrupted';
        if (shouldRetry)
        {
          retried = true;
          speak(null);
          return;
        }

        if (generation === sampleTokenRef.current)
        {
          sampleActiveRef.current = false;
        }
      };

      try
      {
        synth.speak(utterance);
      }
      catch
      {
        if (generation === sampleTokenRef.current)
        {
          sampleActiveRef.current = false;
        }
      }
    };

    const hasConfiguredVoice = normTts(uri) !== '' || normTts(name) !== '';
    if (!hasConfiguredVoice)
    {
      speak(null);
      return;
    }

    let currentVoices: SpeechSynthesisVoice[] = [];
    try
    {
      currentVoices = Array.from(synth.getVoices() || []);
    }
    catch
    {
      currentVoices = [];
    }

    if (currentVoices.length > 0)
    {
      speak(resolveWidgetTtsVoice(uri, name, currentVoices));
      return;
    }

    const token = sampleTokenRef.current;
    const finish = () => {
      if (token !== sampleTokenRef.current)
      {
        return;
      }

      sampleTokenRef.current += 1;
      clearSampleWait();
      let fresh: SpeechSynthesisVoice[] = [];
      try
      {
        fresh = Array.from(synth.getVoices() || []);
      }
      catch
      {
        fresh = [];
      }

      speak(resolveWidgetTtsVoice(uri, name, fresh));
    };

    const listener = () => {
      if (token !== sampleTokenRef.current)
      {
        return;
      }

      let arrived: SpeechSynthesisVoice[] = [];
      try
      {
        arrived = Array.from(synth.getVoices() || []);
      }
      catch
      {
        arrived = [];
      }

      if (arrived.length === 0)
      {
        return;
      }

      finish();
    };

      sampleListenerRef.current = listener;
      synth.addEventListener('voiceschanged', listener);
      sampleTimerRef.current = window.setTimeout(finish, 1000);
    };

    if (replaceSpoken)
    {
      sampleRestartTimerRef.current = window.setTimeout(() => {
        sampleRestartTimerRef.current = null;
        startSample();
      }, 80);
      return;
    }

    startSample();
  };

  const handleSelect = (value: string) => {
    if (value === WIDGET_TTS_SAVED_OPTION)
    {
      return;
    }

    if (!value)
    {
      onChange({ ttsVoiceUri: null, ttsVoiceName: null });
      return;
    }

    const voice = ptBrVoices.find((item) => ttsOptionValue(item) === value);
    if (!voice)
    {
      return;
    }

    const uri = normTts(voice.voiceURI);
    const name = normTts(voice.name);
    onChange({
      ttsVoiceUri: uri ? uri : null,
      ttsVoiceName: name ? name : null,
    });
  };

  let statusText = '';
  if (phase === 'unsupported')
  {
    statusText = 'Este navegador não oferece leitura em voz alta. A voz já salva nesta caixa é mantida.';
  }
  else if (phase === 'loading')
  {
    statusText = 'Carregando vozes deste navegador.';
  }
  else if (ptBrVoices.length === 0)
  {
    statusText = 'Não há voz pt-BR neste navegador.';
  }

  const sentinelSource = normTts(ttsVoiceName) || normTts(ttsVoiceUri);

  return (
    <div className="space-y-2">
      <label className="block text-sm text-gray-700" htmlFor="widget-tts-voice">
        Voz
      </label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select
          id="widget-tts-voice"
          value={selectValue}
          disabled={phase !== 'ready'}
          onChange={(event) => handleSelect(event.target.value)}
          className="w-full min-w-0 flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-50"
        >
          <option value="">Voz padrão do navegador</option>
          {selectValue === WIDGET_TTS_SAVED_OPTION ? (
            <option value={WIDGET_TTS_SAVED_OPTION} title={sentinelSource}>
              {`${sentinelSource} (não disponível neste computador)`}
            </option>
          ) : null}
          {ptBrVoices.map((voice, index) => {
            const value = ttsOptionValue(voice);
            const label = buildTtsVoiceLabel(voice, ptBrVoices);
            return (
              <option key={`${value}-${index}`} value={value} title={voice.name || ''}>
                {label}
              </option>
            );
          })}
        </select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={phase === 'unsupported'}
          onClick={playSample}
        >
          Ouvir amostra
        </Button>
      </div>
      {statusText ? (
        <p className="text-xs text-gray-500">{statusText}</p>
      ) : null}
      <p className="text-xs text-gray-500">{WIDGET_TTS_HELP_TEXT}</p>
    </div>
  );
};
